import { useEffect, useMemo } from 'react';

import { PTTGroup } from '../../../../types/PTTGroup';
import { Device } from '../../../../types/Device';
import { Button, Form, Input, Modal, Select, Typography } from 'antd';
import { Option } from 'antd/lib/mentions';
import { User } from '../../../../types/User';
import './styles.css';

interface GroupModalProps {
    visible: boolean;
    onCancel?: () => void;
    onOk: (values: any) => Promise<void>;
    initialValues?: PTTGroup;
    title: any;
    devices: Device[];
    users?: any[];
    footer?: any;
    form: any;
    formName: string;
    className?: string;
    showCancel?: boolean;
    textOk?: string;
    allGroups?: PTTGroup[];
    currentUser?: User | null;
    isEditMode?: boolean;
}

export const GroupModal: React.FC<GroupModalProps> = ({
    visible,
    onCancel,
    onOk,
    initialValues,
    title,
    devices,
    users,
    footer,
    form,
    formName,
    className,
    showCancel = true,
    textOk,
    allGroups = [],
    currentUser,
    isEditMode = false,
}) => {
    // const [form] = Form.useForm();
    const getAllUsedDevices = useMemo(() => {
        const usedDevices = new Set<string>();
        allGroups.forEach((group) => {
            group.devices?.forEach((device) => {
                usedDevices.add(device.UID);
            });
        });
        return Array.from(usedDevices);
    }, [allGroups]);

    // 3. Функция для проверки, используется ли устройство в других группах
    const isDeviceUsedInOtherGroups = (deviceUID: string, currentGroupUID?: string) => {
        return allGroups.some(
            (group) =>
                group.uid !== currentGroupUID &&
                group.devices?.some((device) => device.UID === deviceUID),
        );
    };

    const filteredUsers = useMemo(() => {
        if (!users) return [];

        if (isEditMode && initialValues?.user) {
            // В режиме редактирования: показываем всех пользователей, кроме создателя комнаты
            // Но также показываем уже выбранных слушателей
            const currentViewers = initialValues.viewers?.map((v) => v.uid) || [];

            return users.filter((user) => {
                // Показываем пользователя если:
                // 1. Он уже выбран в слушателях (даже если он создатель)
                // 2. ИЛИ он не создатель комнаты
                return currentViewers.includes(user.uid) || user.uid !== initialValues.user?.uid;
            });
        } else {
            // В режиме создания: исключаем только текущего пользователя
            return users.filter((user) => user.uid !== currentUser?.uid);
        }
    }, [users, currentUser, isEditMode, initialValues]);

    const availableDevices = useMemo(() => {
        const usedDevices = new Set(getAllUsedDevices);
        return devices.filter((device) => !usedDevices.has(device.UID));
    }, [devices, getAllUsedDevices]);

    useEffect(() => {
        if (initialValues) {
            console.log(initialValues);
            form.setFieldsValue({
                name: initialValues.name,
                devices: initialValues.devices?.map((d) => d.UID),
                viewers: initialValues.viewers?.map((o) => o.uid),
            });
        } else {
            form.resetFields();
        }
    }, [initialValues, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await onOk(values);
            form.resetFields();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const singleButtonFooter = [
        <Button key="submit" type="primary" onClick={handleSubmit} className="buttonAdd">
            {textOk ? textOk : 'ОК'}
        </Button>,
    ];

    return (
        <Modal
            title={title}
            visible={visible}
            onOk={showCancel ? handleSubmit : undefined}
            onCancel={onCancel}
            cancelText={false}
            // cancelText="Отмена"
            // okButtonProps={className={'buttonAdd'}}
            cancelButtonProps={showCancel ? {} : { style: { display: 'none' } }}
            width={600}
            footer={showCancel ? undefined : singleButtonFooter}
            className={className}
            destroyOnClose
        >
            <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                <Form.Item
                    id={formName}
                    label={
                        <span className={'title medium'} style={{ color: 'var(--gray-black)' }}>
                            Название
                        </span>
                    }
                    name="name"
                    rules={[{ required: true, message: 'Введите название группы!' }]}
                >
                    <Input placeholder="Введите название группы" />
                </Form.Item>

                <Form.Item
                    id={formName}
                    label={
                        <span className={'title medium'} style={{ color: 'var(--gray-black)' }}>
                            Устройства
                        </span>
                    }
                    name="devices"
                    rules={[{ required: true, message: 'Выберите хотя бы одно устройство!' }]}
                >
                    <Select
                        mode="multiple"
                        placeholder="Выберите устройства"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            String(option?.children).toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {devices.map((device) => {
                            const isUsed = isDeviceUsedInOtherGroups(
                                device.UID,
                                initialValues?.uid,
                            );
                            return (
                                <Option key={device.UID} value={device.UID} disabled={isUsed}>
                                    {device.name || device.DID}
                                    {isUsed && ' (используется в другой комнате)'}
                                </Option>
                            );
                        })}
                    </Select>
                    {/*<Select mode="multiple" placeholder="Выберите устройства">*/}
                    {/*    {devices.map((device) => (*/}
                    {/*        <Option key={device.UID} value={device.UID}>*/}
                    {/*            {device.name || device.DID}*/}
                    {/*        </Option>*/}
                    {/*    ))}*/}
                    {/*</Select>*/}
                </Form.Item>

                <Form.Item
                    id={formName}
                    label={
                        <span className={'title medium'} style={{ color: 'var(--gray-black)' }}>
                            Слушатели
                        </span>
                    }
                    name="viewers"
                    // rules={[{ required: true, message: 'Выберите хотя бы одного оператора!' }]}
                    rules={[]}
                >
                    <Select mode="multiple" placeholder="Выберите операторов">
                        {filteredUsers?.map((user: User) => (
                            <Option key={String(user.id)} value={user.uid}>
                                {user.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                {initialValues?.user && (
                    <Form.Item
                        label={
                            <span
                                className={'title medium '}
                                style={{
                                    color: 'var(--gray-black)',
                                    whiteSpace: 'pre-line',
                                }}
                            >
                                Создатель аудиокомнаты
                            </span>
                        }
                        className={'creator'}
                    >
                        <span className={'title medium'}>
                            <Input
                                disabled={true}
                                value={initialValues.user.name || initialValues.user.login}
                            />
                        </span>
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};
